import React, { StrictMode, useEffect, Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ToastContainer } from 'react-toastify';
import { ConfirmDialog } from 'primereact/confirmdialog';

// Maintenance Mode Flag - Set to true to enable maintenance mode
const MAINTENANCE_MODE = true;

// Direct imports for critical pages (SEO-important)
import LandingPage from './pages/NewLanding.page';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/Privacy';
import CookiePolicy from './pages/CookiePolicy.page';
import CreateAccount from './pages/CreateAccount.page';
import Login from './pages/Login.page';
import PricingPage from './pages/PricingPage.page';
import NotFoundPage from './pages/NotFoundPage';
import MaintenancePage from './pages/MaintenancePage';

// Lazy loading for internal/protected pages (better performance)
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DataProcessing = lazy(() => import('./pages/DataProcessing.page'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const NewDashboard = lazy(() => import('./pages/NewDashboard'));
const NewSessionTypeSelectionPage = lazy(() => import('./pages/NewSessionTypeSelectionPage'));
const Feedback = lazy(() => import('./pages/Feedback'));
const Calendar = lazy(() => import('./pages/Calendar'));
const NewSession = lazy(() => import('./pages/NewSession'));
const ActiveSession = lazy(() => import('./pages/ActiveSession'));
const SessionDetailsPage = lazy(() => import('./pages/SessionDetailsPage'));
const BillingManagementPage = lazy(() => import('./pages/BillingManagementPage'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const UpcomingPage = lazy(() => import('./pages/UpcomingPage'));
const SessionsList = lazy(() => import('./pages/SessionsList'));
// const PerformanceImprovementPlan = lazy(() => import('./pages/PerformanceImprovementPlan'));
// Job Analysis Pages - Separated for better maintainability
const JobAnalysisListPage = lazy(() => import('./pages/job-analysis/JobAnalysisListPage'));
const JobAnalysisCreatePage = lazy(() => import('./pages/job-analysis/JobAnalysisCreatePage'));
const JobAnalysisSessionPage = lazy(() => import('./pages/job-analysis/JobAnalysisSessionPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProfileManagementPage = lazy(() => import('./pages/ProfileManagementPage'));

const CreditsManagementPage = lazy(() => import('./pages/credits/CreditsManagementPage'));
const CreditDashboard = lazy(() => import('./pages/credits/CreditDashboard'));

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { SessionProvider } from './contexts/session.context';
import { UserProvider } from './contexts/user.context';
import { OnboardingProvider } from './contexts/OnboardingContext';

// Features
import ProtectedRoute from './features/auth/ProtectedRoute';
import { analytics } from './features/firebase/firebase-config';

import 'react-toastify/dist/ReactToastify.css';
import "primereact/resources/themes/lara-light-cyan/theme.css";
import Pip from './pages/PIP';

// Error Boundary
function GlobalErrorBoundary({ error }: { error: Error }) {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Something went wrong!</h2>
      <p>{error.message}</p>
    </div>
  );
}

const STRIPE_KEY = import.meta.env.VITE_STRIPE_API_KEY;
const stripePromise = loadStripe(STRIPE_KEY);

// Loading component for lazy-loaded pages
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Wrapper component for lazy-loaded routes
const LazyRoute = ({ Component }: { Component: React.ComponentType }) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

// Maintenance mode router - shows maintenance page for ALL routes
const maintenanceRouter = createBrowserRouter([
  {
    path: '*',
    element: <MaintenancePage />,
  },
]);

// Normal router - regular app routing
const normalRouter = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
    errorElement: <GlobalErrorBoundary error={new Error('Route Error')} />,
  },
  {
    path: '/terms-and-conditions',
    element: <TermsAndConditions />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/cookies',
    element: <CookiePolicy />,
  },
  {
    path: '/data-processing',
    element: <LazyRoute Component={DataProcessing} />,
  },
  {
    path: '/create-account',
    element: <CreateAccount />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <LazyRoute Component={ForgotPassword} />,
  },
  {
    path: '/onboarding',
    element: <ProtectedRoute component={() => <LazyRoute Component={OnboardingPage} />} />,
  },
  {
    path: '/dashboard',
    element: <ProtectedRoute component={() => <LazyRoute Component={NewDashboard} />} />,
  },
  {
    path: '/session/new/type',
    element: <ProtectedRoute component={() => <LazyRoute Component={NewSessionTypeSelectionPage} />} />,
  },
  {
    path: '/pip',
    element: <ProtectedRoute component={() => <LazyRoute Component={Pip} />} />,
  },
  // {
  // path: '/improvement-plan',
  // element: <ProtectedRoute component={() => <LazyRoute Component={PerformanceImprovementPlan} />} />,
  // },
  {
    path: '/job-analysis',
    element: <ProtectedRoute component={() => <LazyRoute Component={JobAnalysisListPage} />} />,
  },
  {
    path: '/job-analysis/new',
    element: <ProtectedRoute component={() => <LazyRoute Component={JobAnalysisCreatePage} />} />,
  },
  {
    path: '/job-analysis/:sessionId',
    element: <ProtectedRoute component={() => <LazyRoute Component={JobAnalysisSessionPage} />} />,
  },
  {
    path: '/help',
    element: <ProtectedRoute component={() => <LazyRoute Component={Feedback} />} />,
  },
  {
    path: '/calendar',
    element: <ProtectedRoute component={() => <LazyRoute Component={Calendar} />} />,
  },
  {
    path: '/new-session',
    element: <ProtectedRoute component={() => <LazyRoute Component={NewSession} />} />,
  },
  {
    path: '/active-session/:sessionId/:runId',
    element: <ProtectedRoute component={() => <LazyRoute Component={ActiveSession} />} />,
  },
  {
    path: '/session-details/:id',
    element: <ProtectedRoute component={() => <LazyRoute Component={SessionDetailsPage} />} />,
  },
  {
    path: '/billing',
    element: <ProtectedRoute component={() => <LazyRoute Component={BillingManagementPage} />} />,
  },
  {
    path: '/account',
    element: <ProtectedRoute component={() => <LazyRoute Component={MyAccount} />} />,
  },
  {
    path: '/profile',
    element: <ProtectedRoute component={() => <LazyRoute Component={ProfilePage} />} />,
  },
  {
    path: '/profile/manage',
    element: <ProtectedRoute component={() => <LazyRoute Component={ProfileManagementPage} />} />,
  },
  {
    path: '/upcoming',
    element: <ProtectedRoute component={() => <LazyRoute Component={UpcomingPage} />} />,
  },
  {
    path: '/sessions',
    element: <ProtectedRoute component={() => <LazyRoute Component={SessionsList} />} />,
  },
  {
    path: '/pricing',
    element: <PricingPage />,
  },

  {
    path: '/dashboard/credits',
    element: <ProtectedRoute component={() => <LazyRoute Component={CreditDashboard} />} />,
  },
  {
    path: '/dashboard/credits/allowances',
    element: <ProtectedRoute component={() => <LazyRoute Component={CreditsManagementPage} />} />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

const App: React.FC = () => {
  useEffect(() => {
    // Configure analytics based on environment
    if (import.meta.env.VITE_PROD) {
      analytics.setAnalyticsCollectionEnabled(true);
    } else {
      analytics.setAnalyticsCollectionEnabled(false);
    }
  }, []);

  const helmetContext = {};

  return (
    <StrictMode>
      <Elements stripe={stripePromise}>
        <AuthProvider>
          <UserProvider>
            <SessionProvider>
              <OnboardingProvider>
                <HelmetProvider context={helmetContext}>
                  <ToastContainer />
                  <ConfirmDialog />
                  <RouterProvider router={MAINTENANCE_MODE ? maintenanceRouter : normalRouter} />
                </HelmetProvider>
              </OnboardingProvider>
            </SessionProvider>
          </UserProvider>
        </AuthProvider>
      </Elements>
    </StrictMode>
  );
};

export default App;
